import React, { forwardRef } from 'react';
import { Tab as MuiTab, Tabs as MuiTabs } from '@mui/material';
import { isEmpty } from 'lodash';

import { reportErrorInProductionOrThrow } from '~/Utils';

/**
 This Component is a wrapper on MUI component.
 This component should be imported and not MUI directly!
**/

/**
 * Explicit Tabs props based on actual usage in the codebase.
 */
export interface TabsProps {
  /** The value of the currently selected Tab, or `false` when none is selected */
  value?: number | false;
  /** Callback fired when the active tab changes */
  onChange?: (event: React.SyntheticEvent<unknown>, value: number) => void;
  /** Determines additional display behavior of the tabs */
  variant?: 'standard' | 'scrollable' | 'fullWidth';
  /** Determines the color of the indicator */
  indicatorColor?: 'primary' | 'secondary';
  /** Determines the color of the tab text */
  textColor?: 'primary' | 'secondary' | 'inherit';
  /** CSS class name to apply to the root element */
  className?: string;
  /** Inline styles applied to the root element */
  style?: React.CSSProperties;
  /** The Tab children */
  children?: React.ReactNode;
}

/**
 * Explicit Tab props based on actual usage in the codebase.
 *
 * `C` is the root component supplied via the `component` prop (defaulting to `'button'`,
 * the MUI default root). It drives the ref type so callers passing `component={Link}` get
 * an anchor ref, while the default renders a `<button>`.
 */
export interface TabProps<C extends React.ElementType = 'button'> {
  /** The label displayed for the tab */
  label?: React.ReactNode;
  /** Callback fired when the tab is clicked */
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  /** CSS class name to apply to the root element */
  className?: string;
  /** The component used for the root node, e.g. a router Link */
  component?: C;
  /** Route target, forwarded to `component` when it renders a router Link */
  to?: string;
  /** Inline styles applied to the root element */
  style?: React.CSSProperties;
  /** Override or extend the styles applied to the component */
  classes?: Partial<Record<string, string>>;
  // The props below are injected by MUI <Tabs> when it clones its <Tab> children.
  // They are declared so the wrapper forwards them and does not flag them as unexpected.
  fullWidth?: boolean;
  indicator?: React.ReactNode;
  selected?: boolean;
  selectionFollowsFocus?: boolean;
  onChange?: (event: React.SyntheticEvent<unknown>, value: number) => void;
  textColor?: 'primary' | 'secondary' | 'inherit';
  value?: number;
  /**
   * Not the source of the rendered `tabindex` — `Tab` sets that itself, `selected ? 0 : -1`, on
   * every tab. This prop is the rare override: `<Tabs>` passes it only when its `value` is the
   * literal `false` and the tab has no `tabIndex` of its own, giving the first tab a `0` so a group
   * that declares itself unselected still has a keyboard entry point. The `false` is compared
   * strictly, so a group whose `value` merely matches no tab does *not* qualify — measured,
   * `value={false}` gives `0,-1`, while an omitted or non-matching `value` gives `-1,-1`.
   *
   * It is therefore `undefined` on almost every render, which is why the forwarding below spreads
   * it conditionally. Declared for the same reason as the props above: anything `<Tabs>` injects
   * that this interface omits ends up in `restProps` and trips the guard.
   */
  tabIndex?: number;
}

/** The ref type for a given root component, e.g. the router Link's ref or the default <Tab> ref. */
type ComponentRefType<C extends React.ElementType> = React.ComponentPropsWithRef<C>['ref'];

// MUI <Tabs> renders a <div> root and (since mui/material-ui#38391) types its ref accordingly,
// so the forwarded ref passes straight through.
const Tabs = forwardRef<HTMLDivElement, TabsProps>(
  ({ value, onChange, variant, indicatorColor, textColor, className, style, children, ...restProps }, ref) => {
    if (!isEmpty(restProps)) {
      reportErrorInProductionOrThrow(
        `Unexpected props passed to Tabs: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the TabsProps interface and to the component's signature.`,
        true
      );
    }
    return (
      <MuiTabs
        ref={ref}
        value={value}
        onChange={onChange}
        variant={variant}
        indicatorColor={indicatorColor}
        textColor={textColor}
        className={className}
        style={style}
        {...restProps}
      >
        {children}
      </MuiTabs>
    );
  }
);

Tabs.displayName = 'Tabs';

// Generic render function: the ref type is derived from `C`, the component passed via the
// `component` prop. `forwardRef` itself is monomorphic, so the render must stay generic and
// the wrapper is re-typed below with a generic call signature. MUI also mistypes the
// default <Tab> root as `<div>` (`TabTypeMap` declares `defaultComponent = 'div'`) even though
// it renders a `<button>` at runtime — the generic lets each caller name the real element via
// `component` instead of being pinned to MUI's incorrect default.
function TabRender<C extends React.ElementType = 'button'>(
  {
    label,
    onClick,
    className,
    component,
    to,
    style,
    classes,
    fullWidth,
    indicator,
    selected,
    selectionFollowsFocus,
    onChange,
    textColor,
    value,
    tabIndex,
    ...restProps
  }: TabProps<C>,
  ref: ComponentRefType<C>
) {
  if (!isEmpty(restProps)) {
    reportErrorInProductionOrThrow(
      `Unexpected props passed to Tab: ${Object.keys(restProps).join(', ')}.
Examine if these need to be removed, or added explicitly to the TabProps interface and to the component's signature.`,
      true
    );
  }
  // `TabProps` above -- not MUI's own type -- is this wrapper's contract, and the runtime guard
  // enforces it. MUI's public `Tab` type cannot express it: it still declares `div` as the default
  // root while rendering a `<button>`, and it omits every prop `<Tabs>` injects into its cloned
  // children (notably `onChange`, whose `(event, value)` signature collides with the div overload's
  // `FormEventHandler`). So resolve the element against `TabProps` once, here.
  const MuiTabWithWrapperContract = MuiTab as unknown as React.ComponentType<
    TabProps<C> & { ref?: ComponentRefType<C> }
  >;
  const muiTabProps = {
    label,
    onClick,
    className,
    component,
    to,
    style,
    classes,
    fullWidth,
    indicator,
    selected,
    selectionFollowsFocus,
    onChange,
    textColor,
    value,
    // Omitted entirely when absent, rather than passed as `undefined` like the props above. `Tab`
    // sets its own `tabIndex: selected ? 0 : -1` and then spreads whatever it is given on top, so
    // an explicit `undefined` wipes that out and `ButtonBase` falls back to `0` — every tab becomes
    // a tab stop instead of just the selected one.
    ...(tabIndex === undefined ? {} : { tabIndex }),
  };
  return <MuiTabWithWrapperContract ref={ref} {...muiTabProps} {...restProps} />;
}

// `forwardRef` erases the generic parameter, so re-assert it via a generic call signature.
// The intersection with `{ displayName?: string }` keeps the displayName assignment legal.
const Tab = forwardRef(TabRender) as (<C extends React.ElementType = 'button'>(
  props: TabProps<C> & { ref?: ComponentRefType<C> }
) => React.ReactElement | null) & { displayName?: string };

Tab.displayName = 'Tab';

export { Tab, Tabs };
